import React, { useEffect, useState, useMemo } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "moment/locale/pt-br";
import API from "../../utils/API";
import Loader from "../../components/loader";

moment.locale("pt-br");
const localizer = momentLocalizer(moment);

export default function Agenda() {
  const [events, setEvents] = useState([]);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null => create, number => edit
  const isEditing = editingId !== null;

  // Selected slot/event time range
  const [slotRange, setSlotRange] = useState({ start: null, end: null });

  // Form state
  const [cliente, setCliente] = useState("");
  const [carro, setCarro] = useState("");
  const [descricao, setDescricao] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");

  // Calendar view/range
  const [view, setView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());

  // Loaders
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Compute visible range
  const visibleRange = useMemo(() => {
    const d = moment(currentDate);
    if (view === "month") {
      return {
        start: d.clone().startOf("month").startOf("week").toDate(),
        end: d.clone().endOf("month").endOf("week").toDate(),
      };
    }
    if (view === "week") {
      return {
        start: d.clone().startOf("week").toDate(),
        end: d.clone().endOf("week").toDate(),
      };
    }
    return {
      start: d.clone().startOf("day").toDate(),
      end: d.clone().endOf("day").toDate(),
    };
  }, [view, currentDate]);

  // Fetch appointments for visible range
  useEffect(() => {
    const { start, end } = visibleRange;
    if (!start || !end) return;

    const startISO = moment(start).format("YYYY-MM-DD[T]HH:mm:ss");
    const endISO = moment(end).format("YYYY-MM-DD[T]HH:mm:ss");

    const cancel = API.cancelToken();
    setLoadingEvents(true);

    API.getAppointments(cancel, { start: startISO, end: endISO })
      .then((res) => {
        const evs = (res.data || []).map((ev) => ({
          id: ev.appointment_id ?? ev.id,
          title: ev.title ?? `${ev.client_name} - ${ev.description}`,
          start: new Date(ev.start),
          end: new Date(ev.end),
          description: ev.description,
          client_name: ev.client_name, // keep originals for edit prefill
          car: ev.car,
        }));
        setEvents(evs);
      })
      .catch((err) => {
        if (err?.message !== "cancelling") {
          console.error("getAppointments error:", err);
        }
      })
      .finally(() => {
        setLoadingEvents(false);
        API.cancel(cancel);
      });

    return () => API.cancel(cancel);
  }, [visibleRange]);

  // Click existing event → open edit modal
  const handleEventClick = (event) => {
    setEditingId(event.id);

    const s = moment(event.start);
    const e = moment(event.end);

    setSlotRange({ start: s.toDate(), end: e.toDate() });
    setStartTime(s.format("HH:mm"));
    setEndTime(e.format("HH:mm"));

    // Prefill fields
    setCliente(event.client_name ?? event.title?.split(" - ")?.[0] ?? "");
    setCarro(event.car ?? "");
    setDescricao(event.description ?? event.title ?? "");

    setIsModalOpen(true);
  };

  // Click empty slot/day → open create modal
  const handleSelectSlot = ({ start, end }) => {
    setEditingId(null);

    const s = moment(start);
    const e = end ? moment(end) : s.clone();
    const hasTime = s.hour() !== 0 || s.minute() !== 0 || (end && e.diff(s, "minutes") > 0);

    let sFinal = s.clone();
    let eFinal = e.clone();
    if (!hasTime) {
      sFinal = s.clone().hour(9).minute(0).second(0);
      eFinal = s.clone().hour(10).minute(0).second(0);
    } else if (!eFinal.isAfter(sFinal)) {
      eFinal = sFinal.clone().add(1, "hour");
    }

    setSlotRange({ start: sFinal.toDate(), end: eFinal.toDate() });
    setStartTime(sFinal.format("HH:mm"));
    setEndTime(eFinal.format("HH:mm"));

    setCliente("");
    setCarro("");
    setDescricao("");

    setIsModalOpen(true);
  };

  // Build payload from form
  const buildPayload = () => {
    const date = moment(slotRange.start);
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);

    const start = date.clone().hour(sh).minute(sm).second(0);
    let end = date.clone().hour(eh).minute(em).second(0);
    if (!end.isAfter(start)) end = start.clone().add(30, "minutes");

    return {
      client_name: cliente.trim(),
      car: carro.trim(),
      description: descricao.trim(),
      start_datetime: start.format("YYYY-MM-DD[T]HH:mm:ss"),
      end_datetime: end.format("YYYY-MM-DD[T]HH:mm:ss"),
    };
  };

  // Create or Update
  const handleSave = async (e) => {
    e.preventDefault();
    if (!cliente.trim() || !carro.trim() || !descricao.trim() || saving) return;

    setSaving(true);
    const payload = buildPayload();
    const cancel = API.cancelToken();

    try {
      if (isEditing) {
        // UPDATE
        const res = await API.putAppointment(cancel, editingId, payload);
        const ev = res.data;
        const updated = {
          id: ev.appointment_id ?? ev.id,
          title: ev.title ?? `${ev.client_name} - ${ev.description}`,
          start: new Date(ev.start),
          end: new Date(ev.end),
          description: ev.description,
          client_name: ev.client_name,
          car: ev.car,
        };
        setEvents((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
      } else {
        // CREATE
        const res = await API.postAppointment(cancel, payload);
        const ev = res.data;
        const created = {
          id: ev.appointment_id ?? ev.id,
          title: ev.title ?? `${ev.client_name} - ${ev.description}`,
          start: new Date(ev.start),
          end: new Date(ev.end),
          description: ev.description,
          client_name: ev.client_name,
          car: ev.car,
        };
        setEvents((prev) => [...prev, created]);
      }
      setIsModalOpen(false);
    } catch (err) {
      if (err?.message !== "cancelling") {
        console.error("save appointment error:", err);
        alert("Falha ao salvar o agendamento.");
      }
    } finally {
      setSaving(false);
      API.cancel(cancel);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!isEditing || deleting) return;
    if (!window.confirm("Tem certeza que deseja excluir este agendamento?")) return;

    setDeleting(true);
    const cancel = API.cancelToken();

    try {
      await API.deleteAppointment(cancel, editingId, { hard: false });
      setEvents((prev) => prev.filter((e) => e.id !== editingId));
      setIsModalOpen(false);
    } catch (err) {
      if (err?.message !== "cancelling") {
        console.error("delete appointment error:", err);
        alert("Falha ao excluir o agendamento.");
      }
    } finally {
      setDeleting(false);
      API.cancel(cancel);
    }
  };

  // rbc view/date handlers
  const handleRangeChange = (rangeArg) => {
    if (Array.isArray(rangeArg) && rangeArg.length) {
      setCurrentDate(rangeArg[0]);
    } else if (rangeArg && rangeArg.start) {
      setCurrentDate(rangeArg.start);
    }
  };
  const handleNavigate = (date) => setCurrentDate(date);
  const handleView = (nextView) => setView(nextView);

  return (
    <div className="flex flex-col h-screen p-6 bg-gray-100">
      <h1 className="text-3xl font-bold mb-4">Agenda</h1>

      <div className="bg-white p-4 rounded-lg shadow-lg relative">
        {/* Calendar */}
        <div className={loadingEvents ? "pointer-events-none select-none" : ""}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: "80vh" }}
            selectable
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleEventClick}
            onRangeChange={handleRangeChange}
            onNavigate={handleNavigate}
            onView={handleView}
            messages={{
              next: "Próximo",
              previous: "Anterior",
              today: "Hoje",
              month: "Mês",
              week: "Semana",
              day: "Dia",
              agenda: "Agenda",
              noEventsInRange: "Nenhum evento para este período",
            }}
          />
        </div>

        {/* Loader overlay for events fetch */}
        {loadingEvents && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-lg">
            <Loader light={false} />
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white w-full max-w-md rounded-lg shadow-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                {isEditing ? "Editar Agendamento" : "Novo Agendamento"}
              </h2>
              {isEditing && (
                <button
                  onClick={handleDelete}
                  disabled={deleting || saving}
                  className="text-red-600 text-sm disabled:opacity-50"
                  title="Excluir"
                >
                  {deleting ? "Excluindo..." : "Excluir"}
                </button>
              )}
            </div>

            <p className="text-sm text-gray-600 mb-3">
              {slotRange.start &&
                `${moment(slotRange.start).format("DD/MM/YYYY")} (${moment(
                  slotRange.start
                ).format("dddd")})`}
            </p>

            <form onSubmit={handleSave} className={saving ? "opacity-75 pointer-events-none" : ""}>
              {/* Times */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Início</label>
                  <input
                    type="time"
                    className="w-full border rounded-md p-2 mt-1"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fim</label>
                  <input
                    type="time"
                    className="w-full border rounded-md p-2 mt-1"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </div>
              </div>

              <label className="block text-sm font-medium text-gray-700">Nome do Cliente</label>
              <input
                className="w-full border rounded-md p-2 mt-1 mb-3"
                value={cliente}
                onChange={(e) => setCliente(e.target.value)}
                placeholder="Ex.: João da Silva"
              />

              <label className="block text-sm font-medium text-gray-700">Carro</label>
              <input
                className="w-full border rounded-md p-2 mt-1 mb-3"
                value={carro}
                onChange={(e) => setCarro(e.target.value)}
                placeholder="Ex.: Corolla 2020"
              />

              <label className="block text-sm font-medium text-gray-700">
                Descrição Básica do Serviço
              </label>
              <textarea
                className="w-full border rounded-md p-2 mt-1 mb-3"
                rows={3}
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Ex.: Revisão geral e troca de óleo"
              />

              <div className="flex justify-end gap-2 mt-4 items-center">
                {saving && <Loader light={true} />}
                <button
                  type="button"
                  onClick={() => !saving && !deleting && setIsModalOpen(false)}
                  className="px-4 py-2 rounded-md border"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving || deleting || !cliente.trim() || !carro.trim() || !descricao.trim()}
                  className="px-4 py-2 rounded-md bg-lime-700 text-white disabled:opacity-50"
                >
                  {saving ? (isEditing ? "Salvando..." : "Criando...") : (isEditing ? "Salvar" : "Salvar")}
                </button>
              </div>
            </form>

            {/* Optional helper text */}
            {moment(
              `${moment(slotRange.start).format("YYYY-MM-DD")} ${endTime}`,
              "YYYY-MM-DD HH:mm"
            ).isSameOrBefore(
              moment(
                `${moment(slotRange.start).format("YYYY-MM-DD")} ${startTime}`,
                "YYYY-MM-DD HH:mm"
              )
            ) && (
              <p className="text-xs text-red-600 mt-2">
                O horário de fim deve ser depois do início. Ajustarei para +30min ao salvar.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
